import type { VaultPack } from '../types/domain';

/** Unpublished DFY Producer Vault packs seeded into new and existing databases. */
export const DFY_PRODUCER_VAULT_SEEDS: VaultPack[] = [
  {
    id: 'global-rhythm-foundations',
    name: 'Global Rhythm Foundations',
    category: 'Trending Packs',
    description:
      'Dance-forward production tools for Afrobeat, Amapiano, Reggae, Dancehall, and Latin-inspired releases.',
    author: 'Lyrixsa Team',
    version: '1.0',
    releaseDate: 'Sep 8, 2026',
    updated: 'Sep 8, 2026',
    published: false,
    prompts: [
      {
        id: 'grf-p1',
        title: 'Golden Coast Motion',
        genre: 'Afrobeat',
        mood: 'Happy',
        difficulty: 'Beginner',
        text: 'A bright Afrobeat song with interlocking guitars, warm bass, crisp percussion, and joyful call-and-response vocals, building into an unforgettable summer chorus at 106 BPM.',
      },
      {
        id: 'grf-p2',
        title: 'After Hours Log Drum',
        genre: 'Amapiano',
        mood: 'Dreamy',
        difficulty: 'Intermediate',
        text: 'A spacious late-night Amapiano track with rolling log drums, soft piano chords, airy pads, restrained percussion, and intimate vocals at 112 BPM.',
      },
      {
        id: 'grf-p3',
        title: 'Island Fire',
        genre: 'Dancehall',
        mood: 'Energetic',
        difficulty: 'Advanced',
        text: 'A confident Dancehall and Reggae crossover with syncopated drums, deep sub bass, bright guitar chops, crowd-ready vocal phrases, and a high-energy hook at 100 BPM.',
      },
      {
        id: 'grf-p4',
        title: 'Corazon en Movimiento',
        genre: 'Latin',
        mood: 'Romantic',
        difficulty: 'Intermediate',
        text: 'A romantic Latin pop groove with nylon guitar, hand percussion, melodic bass, expressive Spanish vocals, and a danceable chorus at 104 BPM.',
      },
    ],
    presets: [
      {
        id: 'grf-pr1',
        name: 'Afro Sunset',
        tempo: 'Medium',
        mood: 'Happy',
        energy: 72,
        vocals: 'Female vocal',
        structure: 'Intro - Verse - Pre-Chorus - Chorus - Verse - Chorus - Bridge - Final Chorus',
      },
      {
        id: 'grf-pr2',
        name: 'Amapiano Midnight',
        tempo: 'Medium',
        mood: 'Dreamy',
        energy: 58,
        vocals: 'Male vocal',
        structure:
          'Atmospheric Intro - Groove - Verse - Groove - Chorus - Log Drum Break - Final Groove',
      },
    ],
    structures: [
      {
        id: 'grf-st1',
        name: 'Global Radio Arc',
        layout:
          'Percussion Intro - Verse - Pre-Chorus - Chorus - Verse - Chorus - Dance Break - Final Chorus - Outro',
      },
      {
        id: 'grf-st2',
        name: 'Club Groove Extension',
        layout: 'DJ Intro - Groove A - Vocal Verse - Groove B - Breakdown - Peak Groove - DJ Outro',
      },
    ],
    hooks: [
      {
        id: 'grf-h1',
        text: 'When the drums call my name, I come alive again',
        genre: 'Afrobeat',
      },
      {
        id: 'grf-h2',
        text: 'Low lights, log drums, one more dance before sunrise',
        genre: 'Amapiano',
      },
      {
        id: 'grf-h3',
        text: 'Move with the fire, let the whole island know',
        genre: 'Reggae',
      },
    ],
    metadata: [
      {
        id: 'grf-md1',
        platform: 'Spotify',
        title: 'Global rhythm release metadata',
        text: 'Primary genre: Afrobeat. Secondary genres: Amapiano, Dancehall. Moods: joyful, danceable, warm. Suggested playlists: African Heat, Island Pop, Global Dance.',
      },
      {
        id: 'grf-md2',
        platform: 'Apple Music',
        title: 'Amapiano metadata template',
        text: 'Genre: African / Amapiano. Mood: smooth and nocturnal. Instruments: log drum, piano, percussion, bass.',
      },
    ],
    marketing: [
      {
        id: 'grf-mk1',
        platform: 'Instagram Caption',
        title: 'Dance release announcement',
        text: 'The rhythm found us before the sun went down. New music out now on all platforms. #Afrobeat #Amapiano #NewMusic',
      },
      {
        id: 'grf-mk2',
        platform: 'TikTok',
        title: 'Dance challenge launch',
        text: 'Show us your best move when the log drum lands. Use this sound and tag your version.',
      },
    ],
  },
  {
    id: 'modern-pop-songcraft',
    name: 'Modern Pop Songcraft',
    category: 'AI Music Prompts',
    description:
      'Hook-led Pop, Acoustic Pop, and Country Pop ideas designed for clear stories and replayable choruses.',
    author: 'Lyrixsa Team',
    version: '1.1',
    releaseDate: 'Sep 8, 2026',
    updated: 'Sep 8, 2026',
    published: false,
    prompts: [
      {
        id: 'mps-p1',
        title: 'Polaroid Summer',
        genre: 'Pop',
        mood: 'Happy',
        difficulty: 'Beginner',
        text: 'A polished modern pop single about preserving one perfect summer memory, with bright guitars, tight drums, playful synth accents, and a soaring female vocal chorus at 118 BPM.',
      },
      {
        id: 'mps-p2',
        title: 'Kitchen Light Confession',
        genre: 'Acoustic pop',
        mood: 'Emotional',
        difficulty: 'Intermediate',
        text: 'An intimate acoustic pop song set during a midnight conversation, led by fingerpicked guitar, soft piano, subtle strings, and vulnerable close-miked vocals at 84 BPM.',
      },
      {
        id: 'mps-p3',
        title: 'Wildflower Exit',
        genre: 'Country Pop',
        mood: 'Hopeful',
        difficulty: 'Intermediate',
        text: 'A hopeful country pop anthem about leaving a small town without losing your roots, featuring acoustic guitar, mandolin, punchy drums, and a wide singalong chorus at 108 BPM.',
      },
    ],
    presets: [
      {
        id: 'mps-pr1',
        name: 'Radio Pop Lift',
        tempo: 'Medium',
        mood: 'Happy',
        energy: 78,
        vocals: 'Female vocal',
        structure:
          'Cold Open - Verse - Pre-Chorus - Chorus - Verse - Pre-Chorus - Double Chorus - Tag',
      },
      {
        id: 'mps-pr2',
        name: 'Acoustic Confession',
        tempo: 'Slow',
        mood: 'Emotional',
        energy: 42,
        vocals: 'Male vocal',
        structure: 'Guitar Intro - Verse - Refrain - Verse - Chorus - Bridge - Final Chorus',
      },
    ],
    structures: [
      {
        id: 'mps-st1',
        name: 'Three-Minute Pop',
        layout:
          'Hook Intro - Verse 1 - Pre-Chorus - Chorus - Verse 2 - Pre-Chorus - Chorus - Bridge - Final Chorus',
      },
    ],
    hooks: [
      {
        id: 'mps-h1',
        text: 'We were a photograph before we knew the moment would end',
        genre: 'Pop',
      },
      {
        id: 'mps-h2',
        text: 'I told the truth beneath the kitchen light',
        genre: 'Acoustic pop',
      },
    ],
    metadata: [
      {
        id: 'mps-md1',
        platform: 'Apple Music',
        title: 'Contemporary pop metadata',
        text: 'Genre: Pop. Mood: uplifting and nostalgic. Themes: summer, memory, young love. Clean lyrics. Radio-friendly arrangement.',
      },
    ],
    marketing: [
      {
        id: 'mps-mk1',
        platform: 'Facebook Post',
        title: 'Story-led launch post',
        text: 'Some memories fade. This one became a song. Listen to the new single everywhere now.',
      },
      {
        id: 'mps-mk2',
        platform: 'Email Newsletter',
        title: 'Pop single announcement',
        text: 'The new single is here: a bright, honest pop song for anyone still holding onto one unforgettable summer.',
      },
    ],
  },
  {
    id: 'neon-focus-electronics',
    name: 'Neon and Focus Electronics',
    category: 'Genre Presets',
    description:
      'A flexible electronic toolkit spanning Synthwave, Lo-Fi, House, EDM, and cinematic night-drive production.',
    author: 'Lyrixsa Team',
    version: '1.0',
    releaseDate: 'Sep 8, 2026',
    updated: 'Sep 8, 2026',
    published: false,
    prompts: [
      {
        id: 'nfe-p1',
        title: 'Chrome Horizon',
        genre: 'Synthwave',
        mood: 'Energetic',
        difficulty: 'Intermediate',
        text: 'A driving Synthwave instrumental with analog arpeggios, gated drums, wide pads, melodic bass, and a triumphant lead theme at 124 BPM.',
      },
      {
        id: 'nfe-p2',
        title: 'Rain Window Notes',
        genre: 'Lo-fi instrumental',
        mood: 'Calm',
        difficulty: 'Beginner',
        text: 'A calm Lo-Fi instrumental with dusty piano chords, soft vinyl texture, muted drums, mellow bass, and distant rain ambience at 76 BPM.',
      },
      {
        id: 'nfe-p3',
        title: 'First Light Floor',
        genre: 'House',
        mood: 'Hopeful',
        difficulty: 'Advanced',
        text: 'A melodic House track that grows from restrained piano chords into a euphoric sunrise drop, with warm bass, crisp four-on-the-floor drums, and airy vocal chops at 122 BPM.',
      },
      {
        id: 'nfe-p4',
        title: 'Together at the Drop',
        genre: 'EDM',
        mood: 'Epic',
        difficulty: 'Advanced',
        text: 'A festival EDM anthem with emotional verses, tension-building risers, massive layered synths, punchy drums, and a unifying vocal hook at 128 BPM.',
      },
    ],
    presets: [
      {
        id: 'nfe-pr1',
        name: 'Night Drive Pulse',
        tempo: 'Fast',
        mood: 'Energetic',
        energy: 84,
        vocals: 'Instrumental only',
        structure: 'Intro - Theme - Build - Main Theme - Break - Final Theme - Outro',
      },
      {
        id: 'nfe-pr2',
        name: 'Study Tape',
        tempo: 'Slow',
        mood: 'Calm',
        energy: 24,
        vocals: 'Instrumental only',
        structure: 'Texture Intro - Loop A - Loop B - Piano Variation - Loop A - Fade',
      },
      {
        id: 'nfe-pr3',
        name: 'Festival Unity',
        tempo: 'Fast',
        mood: 'Epic',
        energy: 94,
        vocals: 'Female vocal',
        structure: 'Vocal Intro - Verse - Build - Drop - Break - Build - Final Drop - Outro',
      },
    ],
    structures: [
      {
        id: 'nfe-st1',
        name: 'Electronic Peak Arc',
        layout: 'DJ Intro - Theme - Build - Drop - Breakdown - Second Build - Peak Drop - DJ Outro',
      },
    ],
    hooks: [
      {
        id: 'nfe-h1',
        text: 'We become electric when the city sleeps',
        genre: 'Synthwave',
      },
      {
        id: 'nfe-h2',
        text: 'Meet me where the first light hits the floor',
        genre: 'House',
      },
    ],
    metadata: [
      {
        id: 'nfe-md1',
        platform: 'Beatport',
        title: 'Club release metadata',
        text: 'Primary genre: Melodic House. BPM: 122. Key mood: hopeful. DJ-friendly intro and outro. Peak-time compatibility: warm-up to main room.',
      },
      {
        id: 'nfe-md2',
        platform: 'Spotify',
        title: 'Focus instrumental metadata',
        text: 'Genre: Lo-Fi instrumental. Mood: calm, focused, rainy. Suggested playlists: Focus Flow, Study Beats, Reading Soundtrack.',
      },
    ],
    marketing: [
      {
        id: 'nfe-mk1',
        platform: 'Instagram Reel',
        title: 'Drop preview caption',
        text: 'Headphones on. City lights up. The new electronic release is live now.',
      },
    ],
  },
  {
    id: 'bars-drums-and-truth',
    name: 'Bars, Drums and Truth',
    category: 'Hook Library',
    description:
      'Narrative Hip Hop, Trap, and melodic rap resources for sharp verses, memorable hooks, and modern production.',
    author: 'Lyrixsa Team',
    version: '1.2',
    releaseDate: 'Sep 8, 2026',
    updated: 'Sep 8, 2026',
    published: false,
    prompts: [
      {
        id: 'bdt-p1',
        title: 'Receipts and Rooftops',
        genre: 'Hip hop',
        mood: 'Hopeful',
        difficulty: 'Intermediate',
        text: 'A reflective Hip Hop track about turning setbacks into proof of progress, with soulful samples, firm drums, warm bass, vivid storytelling verses, and a melodic hook at 92 BPM.',
      },
      {
        id: 'bdt-p2',
        title: 'No Witnesses',
        genre: 'Trap',
        mood: 'Dark',
        difficulty: 'Advanced',
        text: 'A dark modern Trap record with sparse piano, sliding 808s, sharp hi-hats, tense pauses, confident rap delivery, and a compact chant hook at 142 BPM.',
      },
      {
        id: 'bdt-p3',
        title: 'Letters I Never Sent',
        genre: 'Hip Hop',
        mood: 'Emotional',
        difficulty: 'Advanced',
        text: 'An emotional melodic Hip Hop song framed as an unsent letter, using soft guitar, restrained drums, deep bass, conversational verses, and a vulnerable sung chorus at 88 BPM.',
      },
    ],
    presets: [
      {
        id: 'bdt-pr1',
        name: 'Soul Sample Testimony',
        tempo: 'Medium',
        mood: 'Hopeful',
        energy: 66,
        vocals: 'Male vocal',
        structure: 'Sample Intro - Verse 1 - Hook - Verse 2 - Hook - Bridge - Final Hook',
      },
    ],
    structures: [
      {
        id: 'bdt-st1',
        name: 'Sixteen and Eight',
        layout:
          'Cold Open - Verse 1 (16 bars) - Hook (8 bars) - Verse 2 (16 bars) - Hook - Flow Switch - Final Hook',
      },
      {
        id: 'bdt-st2',
        name: 'Melodic Rap Letter',
        layout:
          'Guitar Intro - Half Verse - Chorus - Full Verse - Chorus - Confessional Bridge - Final Chorus',
      },
    ],
    hooks: [
      {
        id: 'bdt-h1',
        text: 'I kept every receipt from the days they counted me out',
        genre: 'Hip hop',
      },
      {
        id: 'bdt-h2',
        text: 'No witnesses, just the work and the scars',
        genre: 'Trap',
      },
      {
        id: 'bdt-h3',
        text: 'This is the letter that I never learned to send',
        genre: 'Hip Hop',
      },
      {
        id: 'bdt-h4',
        text: 'Built from the pressure, still standing on purpose',
        genre: 'Hip hop',
      },
    ],
    metadata: [
      {
        id: 'bdt-md1',
        platform: 'Spotify',
        title: 'Narrative rap metadata',
        text: 'Genre: Hip Hop. Mood: reflective, resilient. Themes: ambition, setbacks, personal growth. Suggested playlists: Mind Right, Bars and Beats, Motivation Mix.',
      },
    ],
    marketing: [
      {
        id: 'bdt-mk1',
        platform: 'X Post',
        title: 'Bar teaser',
        text: 'One line from the new record: I kept every receipt from the days they counted me out. Full track available now.',
      },
      {
        id: 'bdt-mk2',
        platform: 'TikTok',
        title: 'Open verse prompt',
        text: 'The second verse is open. Add your story, use this sound, and tag the release.',
      },
    ],
  },
  {
    id: 'velvet-rnb-and-soul',
    name: 'Velvet R&B and Soul',
    category: 'AI Music Prompts',
    description:
      'Warm R&B and Soul writing tools for intimate vocals, honest relationships, and expressive live arrangements.',
    author: 'Lyrixsa Team',
    version: '1.0',
    releaseDate: 'Sep 8, 2026',
    updated: 'Sep 8, 2026',
    published: false,
    prompts: [
      {
        id: 'vrs-p1',
        title: 'Slow Truth',
        genre: 'R&B',
        mood: 'Romantic',
        difficulty: 'Intermediate',
        text: 'A slow-burning contemporary R&B song about finally saying what both people already know, with Rhodes chords, deep bass, rim-click drums, layered harmonies, and intimate female vocals at 74 BPM.',
      },
      {
        id: 'vrs-p2',
        title: 'Tender Mercy',
        genre: 'Soul',
        mood: 'Emotional',
        difficulty: 'Advanced',
        text: 'A timeless Soul ballad about choosing compassion after conflict, led by piano, organ, restrained strings, live drums, and a powerful male vocal that rises into a choir-backed final chorus at 68 BPM.',
      },
      {
        id: 'vrs-p3',
        title: 'Daylight Distance',
        genre: 'R&B',
        mood: 'Dreamy',
        difficulty: 'Advanced',
        text: 'An alternative R&B track about intimacy feeling different in daylight, with detuned keys, atmospheric guitar, minimal drums, sub bass, breathy vocals, and an unresolved outro at 82 BPM.',
      },
    ],
    presets: [
      {
        id: 'vrs-pr1',
        name: 'Velvet Slow Jam',
        tempo: 'Slow',
        mood: 'Romantic',
        energy: 38,
        vocals: 'Female vocal',
        structure: 'Intro - Verse - Pre-Chorus - Chorus - Verse - Chorus - Bridge - Vocal Outro',
      },
      {
        id: 'vrs-pr2',
        name: 'Church Organ Soul',
        tempo: 'Slow',
        mood: 'Emotional',
        energy: 62,
        vocals: 'Male vocal',
        structure:
          'Piano Intro - Verse - Refrain - Verse - Chorus - Bridge - Choir Lift - Final Chorus',
      },
    ],
    structures: [
      {
        id: 'vrs-st1',
        name: 'Slow Burn Reveal',
        layout:
          'Atmospheric Intro - Verse 1 - Pre-Chorus - Chorus - Verse 2 - Chorus - Confession Bridge - Final Chorus - Ad-libs',
      },
    ],
    hooks: [
      {
        id: 'vrs-h1',
        text: 'Say it slowly, I already know the truth',
        genre: 'R&B',
      },
      {
        id: 'vrs-h2',
        text: 'Mercy sounds sweeter when it comes from you',
        genre: 'Soul',
      },
    ],
    metadata: [
      {
        id: 'vrs-md1',
        platform: 'Apple Music',
        title: 'R&B release metadata',
        text: 'Genre: R&B / Soul. Mood: intimate, romantic, reflective. Vocal style: close and expressive. Suggested playlists: Brown Sugar, Slow Jams, Breaking R&B.',
      },
    ],
    marketing: [
      {
        id: 'vrs-mk1',
        platform: 'Instagram Caption',
        title: 'Late-night release caption',
        text: 'For the conversations that only happen after midnight. The new R&B single is yours now.',
      },
      {
        id: 'vrs-mk2',
        platform: 'Email Newsletter',
        title: 'Soul release note',
        text: 'A song about grace, repair, and the courage to soften. Listen to the new Soul release today.',
      },
    ],
  },
  {
    id: 'guitars-roots-and-roads',
    name: 'Guitars, Roots and Roads',
    category: 'Song Structures',
    description:
      'Songwriting and production frameworks for Rock, Country, Indie Folk, and guitar-led storytelling.',
    author: 'Lyrixsa Team',
    version: '1.0',
    releaseDate: 'Sep 8, 2026',
    updated: 'Sep 8, 2026',
    published: false,
    prompts: [
      {
        id: 'grr-p1',
        title: 'Break the Silence',
        genre: 'Rock',
        mood: 'Aggressive',
        difficulty: 'Advanced',
        text: 'A modern Rock anthem about refusing to stay quiet, with distorted guitars, driving bass, hard live drums, tense verses, and a huge shouted chorus at 138 BPM.',
      },
      {
        id: 'grr-p2',
        title: 'County Line Morning',
        genre: 'Country',
        mood: 'Hopeful',
        difficulty: 'Beginner',
        text: 'A hopeful Country song about crossing a county line toward a new life, featuring acoustic guitar, pedal steel, steady drums, vivid road imagery, and warm male vocals at 96 BPM.',
      },
      {
        id: 'grr-p3',
        title: 'Family Tree Rings',
        genre: 'Indie Folk',
        mood: 'Emotional',
        difficulty: 'Intermediate',
        text: 'An intimate Indie Folk song about inherited stories, with fingerpicked guitar, cello, brushed percussion, close harmonies, and a final verse that returns to the family home at 80 BPM.',
      },
    ],
    presets: [
      {
        id: 'grr-pr1',
        name: 'Arena Guitar Rise',
        tempo: 'Fast',
        mood: 'Epic',
        energy: 90,
        vocals: 'Male vocal',
        structure:
          'Riff Intro - Verse - Pre-Chorus - Chorus - Verse - Chorus - Solo - Bridge - Final Chorus',
      },
      {
        id: 'grr-pr2',
        name: 'Front Porch Story',
        tempo: 'Medium',
        mood: 'Hopeful',
        energy: 50,
        vocals: 'Female vocal',
        structure: 'Acoustic Intro - Verse - Chorus - Verse - Chorus - Story Bridge - Final Chorus',
      },
    ],
    structures: [
      {
        id: 'grr-st1',
        name: 'Rock Escalation',
        layout:
          'Riff Intro - Restrained Verse - Pre-Chorus - Full Chorus - Verse - Chorus - Guitar Solo - Half-Time Bridge - Double Chorus',
      },
      {
        id: 'grr-st2',
        name: 'Country Resolution',
        layout:
          'Scene-Setting Verse - Chorus - Story Verse - Chorus - Reflective Bridge - Final Chorus - Instrumental Tag',
      },
    ],
    hooks: [
      {
        id: 'grr-h1',
        text: 'Turn up the truth until it breaks the silence',
        genre: 'Rock',
      },
      {
        id: 'grr-h2',
        text: 'The county line looked like a sunrise from here',
        genre: 'Country',
      },
      {
        id: 'grr-h3',
        text: 'Every ring in the old oak knows our name',
        genre: 'Indie Folk',
      },
    ],
    metadata: [
      {
        id: 'grr-md1',
        platform: 'Spotify',
        title: 'Guitar music metadata',
        text: 'Genres: Rock, Country, Indie Folk. Themes: resilience, roads, home, identity. Instruments: electric guitar, acoustic guitar, live drums, pedal steel.',
      },
    ],
    marketing: [
      {
        id: 'grr-mk1',
        platform: 'YouTube Description',
        title: 'Live-band release description',
        text: 'A guitar-led story about finding your voice and carrying home with you. Stream and share the new release.',
      },
    ],
  },
  {
    id: 'blue-notes-after-dark',
    name: 'Blue Notes After Dark',
    category: 'Genre Presets',
    description:
      'Late-night Jazz and Blues resources built around live-room detail, improvisation, and emotionally direct writing.',
    author: 'Lyrixsa Team',
    version: '1.0',
    releaseDate: 'Sep 8, 2026',
    updated: 'Sep 8, 2026',
    published: false,
    prompts: [
      {
        id: 'bnad-p1',
        title: 'Last Table Open',
        genre: 'Jazz',
        mood: 'Romantic',
        difficulty: 'Advanced',
        text: 'A smoky late-night Jazz vocal about two strangers at the last open table, with upright bass, brushed drums, piano, muted trumpet responses, and elegant female vocals at 72 BPM.',
      },
      {
        id: 'bnad-p2',
        title: 'Empty Pocket Morning',
        genre: 'Blues',
        mood: 'Emotional',
        difficulty: 'Intermediate',
        text: 'A raw electric Blues song about facing another morning with empty pockets and stubborn humor, featuring gritty guitar, Hammond organ, shuffle drums, and weathered male vocals at 88 BPM.',
      },
      {
        id: 'bnad-p3',
        title: 'Second Chance Standard',
        genre: 'Jazz',
        mood: 'Hopeful',
        difficulty: 'Advanced',
        text: 'A graceful Jazz standard about asking for a second chance, with piano trio, warm saxophone, tasteful harmonic turns, and a restrained vocal performance at 78 BPM.',
      },
    ],
    presets: [
      {
        id: 'bnad-pr1',
        name: 'Midnight Quartet',
        tempo: 'Slow',
        mood: 'Romantic',
        energy: 36,
        vocals: 'Female vocal',
        structure: 'Rubato Intro - Head - Verse - Refrain - Solo - Verse - Final Refrain - Tag',
      },
      {
        id: 'bnad-pr2',
        name: 'Working Blues Shuffle',
        tempo: 'Medium',
        mood: 'Emotional',
        energy: 61,
        vocals: 'Male vocal',
        structure:
          'Guitar Turnaround - Verse - Verse - Refrain - Solo - Stop-Time Verse - Final Refrain',
      },
    ],
    structures: [
      {
        id: 'bnad-st1',
        name: 'Jazz Standard Form',
        layout: 'Intro - Head - Vocal A - Vocal B - Instrumental Solo - Vocal A - Refrain - Tag',
      },
    ],
    hooks: [
      {
        id: 'bnad-h1',
        text: 'There is one last table and one last song',
        genre: 'Jazz',
      },
      {
        id: 'bnad-h2',
        text: 'My pockets came up empty but the morning came on time',
        genre: 'Blues',
      },
    ],
    metadata: [
      {
        id: 'bnad-md1',
        platform: 'Apple Music',
        title: 'Jazz and Blues metadata',
        text: 'Genres: Vocal Jazz / Electric Blues. Mood: nocturnal, intimate, resilient. Recording character: warm live room with natural dynamics.',
      },
      {
        id: 'bnad-md2',
        platform: 'Bandcamp',
        title: 'Musician credits template',
        text: 'Lead vocal: [Name]. Piano or guitar: [Name]. Bass: [Name]. Drums: [Name]. Horns: [Name]. Recorded at: [Studio].',
      },
    ],
    marketing: [
      {
        id: 'bnad-mk1',
        platform: 'Facebook Post',
        title: 'Late-night listening post',
        text: 'Dim the room and stay for one more song. Blue Notes After Dark is streaming now.',
      },
    ],
  },
  {
    id: 'orchestral-screen-worlds',
    name: 'Orchestral Screen Worlds',
    category: 'Song Structures',
    description:
      'Classical and Cinematic blueprints for emotional scenes, trailers, character themes, and instrumental storytelling.',
    author: 'Lyrixsa Team',
    version: '1.1',
    releaseDate: 'Sep 8, 2026',
    updated: 'Sep 8, 2026',
    published: false,
    prompts: [
      {
        id: 'osw-p1',
        title: 'Kingdom at First Light',
        genre: 'Cinematic',
        mood: 'Epic',
        difficulty: 'Advanced',
        text: 'An epic Cinematic score for a kingdom awakening at first light, beginning with solo horn and low strings before expanding into full orchestra, choir, and thunderous percussion at 86 BPM.',
      },
      {
        id: 'osw-p2',
        title: 'Letters for Solo Cello',
        genre: 'Classical',
        mood: 'Emotional',
        difficulty: 'Advanced',
        text: 'A lyrical Classical chamber piece shaped like an unanswered letter, led by solo cello with piano and a small string ensemble, moving from grief toward quiet acceptance.',
      },
      {
        id: 'osw-p3',
        title: 'The Last Signal',
        genre: 'Cinematic',
        mood: 'Dark',
        difficulty: 'Intermediate',
        text: 'A tense Cinematic underscore for the final signal from a distant station, using pulsing low strings, restrained electronic texture, sparse piano, and one overwhelming orchestral impact.',
      },
    ],
    presets: [
      {
        id: 'osw-pr1',
        name: 'Heroic Dawn',
        tempo: 'Medium',
        mood: 'Epic',
        energy: 88,
        vocals: 'Instrumental only',
        structure:
          'Motif - Development - Percussion Rise - Full Theme - Quiet Reprise - Final Statement',
      },
      {
        id: 'osw-pr2',
        name: 'Chamber Reflection',
        tempo: 'Slow',
        mood: 'Emotional',
        energy: 32,
        vocals: 'Instrumental only',
        structure: 'Solo Opening - Theme - Variation - Countermelody - Return - Coda',
      },
    ],
    structures: [
      {
        id: 'osw-st1',
        name: 'Trailer Three-Act Rise',
        layout:
          'Identity Motif - Tension Bed - First Impact - Emotional Pause - Accelerating Rise - Final Impact - Logo Tail',
      },
      {
        id: 'osw-st2',
        name: 'Classical Theme and Variations',
        layout: 'Theme - Variation I - Variation II - Contrasting Middle - Theme Return - Coda',
      },
    ],
    hooks: [
      {
        id: 'osw-h1',
        text: 'A four-note brass motif that rises, pauses, and resolves upward',
        genre: 'Cinematic',
      },
      {
        id: 'osw-h2',
        text: 'A descending cello phrase answered by two quiet piano notes',
        genre: 'Classical',
      },
    ],
    metadata: [
      {
        id: 'osw-md1',
        platform: 'Music Library',
        title: 'Sync metadata template',
        text: 'Style: Cinematic orchestral. Mood arc: tense to triumphant. Edit points: 0:30, 0:60, 0:90. Instruments: strings, brass, choir, percussion. Suitable for trailer and dramatic montage.',
      },
    ],
    marketing: [
      {
        id: 'osw-mk1',
        platform: 'YouTube Description',
        title: 'Score showcase description',
        text: 'An original orchestral journey from a solitary motif to a full cinematic climax. Available for listening and commercial licensing.',
      },
      {
        id: 'osw-mk2',
        platform: 'LinkedIn Post',
        title: 'Composer portfolio post',
        text: 'New score release: a three-act orchestral cue developed for trailers, games, film, and visual storytelling.',
      },
    ],
  },
  {
    id: 'voices-of-faith',
    name: 'Voices of Faith',
    category: 'Seasonal Packs',
    description:
      'Gospel and contemporary worship resources for testimony, communal singing, hope, and choir-led celebration.',
    author: 'Lyrixsa Team',
    version: '1.0',
    releaseDate: 'Sep 8, 2026',
    updated: 'Sep 8, 2026',
    published: false,
    prompts: [
      {
        id: 'vof-p1',
        title: 'Grace Found Me',
        genre: 'Gospel',
        mood: 'Hopeful',
        difficulty: 'Intermediate',
        text: 'A contemporary Gospel testimony about grace arriving at the lowest moment, with piano, Hammond organ, live drums, expressive lead vocals, choir responses, and a powerful vamp at 78 BPM.',
      },
      {
        id: 'vof-p2',
        title: 'Victory Morning',
        genre: 'Gospel',
        mood: 'Happy',
        difficulty: 'Advanced',
        text: 'A joyful Gospel celebration with handclaps, organ, bright brass, energetic drums, a commanding female lead, and a full choir lifting the final key change at 112 BPM.',
      },
      {
        id: 'vof-p3',
        title: 'Still I Believe',
        genre: 'Gospel',
        mood: 'Emotional',
        difficulty: 'Beginner',
        text: 'A quiet worship ballad about holding faith through uncertainty, led by piano and ambient guitar with gentle male vocals, simple congregational lyrics, and a gradual final chorus at 70 BPM.',
      },
    ],
    presets: [
      {
        id: 'vof-pr1',
        name: 'Choir Testimony',
        tempo: 'Medium',
        mood: 'Hopeful',
        energy: 74,
        vocals: 'Female vocal',
        structure:
          'Piano Intro - Verse - Chorus - Testimony Verse - Chorus - Bridge - Vamp - Key Change - Final Chorus',
      },
    ],
    structures: [
      {
        id: 'vof-st1',
        name: 'Testimony to Vamp',
        layout:
          'Intro - Verse 1 - Chorus - Verse 2 - Chorus - Spoken Testimony - Bridge - Choir Vamp - Final Chorus',
      },
    ],
    hooks: [
      {
        id: 'vof-h1',
        text: 'Grace found me where the light could barely reach',
        genre: 'Gospel',
      },
      {
        id: 'vof-h2',
        text: 'This is the morning victory learned my name',
        genre: 'Gospel',
      },
      {
        id: 'vof-h3',
        text: 'Even in the waiting, still I believe',
        genre: 'Gospel',
      },
    ],
    metadata: [
      {
        id: 'vof-md1',
        platform: 'Spotify',
        title: 'Gospel release metadata',
        text: 'Genre: Gospel / Contemporary Worship. Moods: hopeful, uplifting, devotional. Themes: grace, perseverance, testimony, victory. Suggested playlists: Gospel Rising, Worship Now.',
      },
    ],
    marketing: [
      {
        id: 'vof-mk1',
        platform: 'Facebook Post',
        title: 'Community release post',
        text: 'A new song of grace, hope, and testimony is here. Share it with someone who needs the reminder today.',
      },
      {
        id: 'vof-mk2',
        platform: 'Instagram Caption',
        title: 'Choir moment caption',
        text: 'When the choir enters, the whole testimony changes. Voices of Faith is out now. #GospelMusic #Worship',
      },
    ],
  },
  {
    id: 'emotion-to-anthem',
    name: 'Emotion to Anthem',
    category: 'Marketing Assets',
    description:
      'Cross-genre release concepts that turn personal stories into Pop, Rock, R&B, and cinematic anthems.',
    author: 'Lyrixsa Team',
    version: '1.0',
    releaseDate: 'Sep 8, 2026',
    updated: 'Sep 8, 2026',
    published: false,
    prompts: [
      {
        id: 'eta-p1',
        title: 'Start Again Loudly',
        genre: 'Pop',
        mood: 'Hopeful',
        difficulty: 'Beginner',
        text: 'A hopeful Pop anthem about choosing a new beginning in public, with pulsing synths, rhythmic guitars, strong drums, direct verses, and a chorus designed for group singing at 120 BPM.',
      },
      {
        id: 'eta-p2',
        title: 'After the Fire',
        genre: 'Rock',
        mood: 'Epic',
        difficulty: 'Advanced',
        text: 'An epic Rock song about rebuilding after everything familiar is gone, with brooding guitar verses, explosive drums, rising strings, and a final chorus that feels larger than the opening at 128 BPM.',
      },
      {
        id: 'eta-p3',
        title: 'Call Me by Daylight',
        genre: 'R&B',
        mood: 'Romantic',
        difficulty: 'Intermediate',
        text: 'A modern R&B song asking for a love that exists outside late-night messages, featuring warm keys, clean guitar, minimal percussion, rich harmonies, and a confident chorus at 86 BPM.',
      },
      {
        id: 'eta-p4',
        title: 'One Last Horizon',
        genre: 'Cinematic',
        mood: 'Emotional',
        difficulty: 'Advanced',
        text: 'A cinematic pop farewell that begins with piano and solo vocal, then expands through strings, drums, choir, and a final suspended note that leaves the ending open.',
      },
    ],
    presets: [
      {
        id: 'eta-pr1',
        name: 'Personal to Universal',
        tempo: 'Medium',
        mood: 'Hopeful',
        energy: 80,
        vocals: 'Female vocal',
        structure:
          'Personal Verse - Rising Pre-Chorus - Universal Chorus - Contrast Verse - Chorus - Quiet Bridge - Final Anthem',
      },
    ],
    structures: [
      {
        id: 'eta-st1',
        name: 'Emotional Expansion',
        layout:
          'Solo Opening - Verse - Pre-Chorus - Full Chorus - Verse - Chorus - Stripped Bridge - Orchestral Final Chorus - Tail',
      },
    ],
    hooks: [
      {
        id: 'eta-h1',
        text: 'If I start again, I am starting where everyone can see',
        genre: 'Pop',
      },
      {
        id: 'eta-h2',
        text: 'We are what grows after the fire',
        genre: 'Rock',
      },
    ],
    metadata: [
      {
        id: 'eta-md1',
        platform: 'Spotify',
        title: 'Anthem positioning template',
        text: 'Primary mood: hopeful. Secondary mood: emotional. Themes: reinvention, resilience, public courage. Audience: motivational and cinematic-pop listeners.',
      },
    ],
    marketing: [
      {
        id: 'eta-mk1',
        platform: 'Instagram Carousel',
        title: 'Five-slide song story',
        text: 'Slide 1: the lyric that started it. Slide 2: the moment behind it. Slide 3: a studio detail. Slide 4: the chorus reveal. Slide 5: listen now.',
      },
      {
        id: 'eta-mk2',
        platform: 'TikTok',
        title: 'Before-and-after teaser',
        text: 'Begin with the quiet first line, cut to the final chorus, and caption it: This is what healing sounds like when it gets loud.',
      },
      {
        id: 'eta-mk3',
        platform: 'Email Newsletter',
        title: 'Release story email',
        text: 'This song began as one private sentence and grew into something meant to be sung together. Hear the full release now.',
      },
    ],
  },
];
